import { useState, useEffect } from 'react';

/**
 * Use matchMedia to create a listener on a specific breakpoint.
 * This is more performant than a resize listener as it listens just for the moment the viewport crosses the threshold.
 * @param breakpoint - window.innerWidth px
 * @param compareBy - 'LT' ('less than') | 'GT' ('greater than')
 * @returns boolean
 */
export const useBreakpointListener = (
    breakpoint = 1232,
    compareBy: 'LT' | 'GT' = 'LT'
): boolean => {
    const isSSR = typeof window === 'undefined';

    // default to mobile screenSize if on server.
    const width = isSSR ? 480 : window.innerWidth;
    const [isMatching, setIsMatching] = useState(
        compareBy === 'LT' ? width < breakpoint : width > breakpoint
    );

    useEffect(() => {
        const mediaQuery =
            compareBy === 'LT'
                ? `(max-width: ${breakpoint - 1}px)`
                : `(min-width: ${breakpoint})`;

        const mediaQueryList: MediaQueryList = window.matchMedia(mediaQuery);
        const handler = (event: MediaQueryListEvent) => {
            setIsMatching(!!event.matches);
        };

        // Older Webkit browsers don't support 'addEventListener'
        if (mediaQueryList.addEventListener) {
            mediaQueryList.addEventListener('change', handler);
        } else {
            mediaQueryList.addListener(handler);
        }
        return () => {
            if (mediaQueryList.removeEventListener) {
                mediaQueryList.removeEventListener('change', handler);
            } else {
                mediaQueryList.removeListener(handler);
            }
        };
    }, [breakpoint, compareBy]);

    return isMatching;
};
